import React from 'react';
import moment from 'moment';
import { fieldsContext, SENDER, signatureRequestContext, SignatureRequestContext } from 'hellospa/components/signature-request';
import { useContextCacher } from 'common/utils/context';
import { useConditionalLogic } from 'hellospa/conditional-logic';
import { useShallowCache } from 'hellospa/common/hooks';
import useSkipHiddenFields from './use-skip-hidden-fields';
import { autofillFieldsTitle } from 'hellospa/components/editor/constants';
import { Field, FieldTypes, RadioField } from '../../editor/types';
import { defineMessages, useIntl } from 'react-intl';
import { unreachable } from 'hellospa/ts-utils';
import { useLocalBanner } from '../../notification-banner/data/actions';
import { NotificationBannerType } from 'hellospa/components/notification-banner';
import { useDispatch } from 'react-redux';
import { FieldUpdate, SignatureRequest, SignerContextShape } from '../types';

const messages = defineMessages({
  hiddenFieldsNotification: {
    id: '',
    description: 'notification in webpage asking user to double check to ensure relevent information is present',
    defaultMessage: 'This document is adaptive and will show/hide fields depending on your answers to ensure you\'re only filling in relevant information! Make sure to double check your answers before submitting.',
  },
  alertTextAutofill: {
    id: '',
    description: 'signer app, autofillable fields, notification to user, informs user that changing selected field will change automatically N-number of fields of same type',
    defaultMessage: `{fieldNumber, plural,
      one {Changes you make in this field will be reflected in # other {autoFillType} field}
      other {Changes you make in this field will be reflected in # other {autoFillType} fields}}`,
  },
  validationPreview: {
    id: '',
    description: 'A banner message to show when users encounter a validated field',
    defaultMessage: 'This field has validation applied to it but it can’t be shown in the preview.',
  },
  mergeFieldPreview: {
    id: '',
    description: 'A banner message to show when users encounter a merge field',
    defaultMessage: 'Merge fields are interactive in this preview to allow you to test field formatting and placement, but won’t be editable during actual signing. Data is passed into these fields at the time of sending',
  },
});

function ValidationNotification() {
  const f = React.useContext(signatureRequestContext);
  const fields = React.useContext(fieldsContext);
  const { createBannerMessage, removeBannerMessage } = useLocalBanner();

  const currentField = React.useMemo(() => {
    const selectedFields = f.selectedFieldIds.map(
      (id) => fields.find((f) => f.id === id),
    );
    if (selectedFields.length === 1) {
      return selectedFields[0];
    }
    return null;
  }, [f.selectedFieldIds, fields]);

  const showValidationBanner = (
    currentField
    && currentField.type === FieldTypes.Text
    && currentField.validationType
  );

  const showMergeFieldBanner = (
    currentField
    && currentField.signer === SENDER
  );

  React.useEffect(() => {
    if (showValidationBanner) {
      const id = createBannerMessage(
        messages.validationPreview,
        NotificationBannerType.Info,
        {},
        { key: 'validation-preview' },
      );
      return () => removeBannerMessage(id);
    }
    if (showMergeFieldBanner) {
      const id = createBannerMessage(
        messages.mergeFieldPreview,
        NotificationBannerType.Info,
        {},
        { key: 'merge-field-preview' },
      );
      return () => removeBannerMessage(id);
    }


    return () => { };
    // eslint-disable-next-line max-len
  }, [createBannerMessage, currentField, removeBannerMessage, showMergeFieldBanner, showValidationBanner]);

  return null;
}

type Props = {
  signatureRequest: SignatureRequest,
  fieldResetKey?: string,
  children: React.ReactNode,
  selectedFieldIds: Array<Field['id']>,
  setCurrentField: SignerContextShape['selectField'],
  onFieldUpdate?: (field: Field, updates: Partial<Field>) => void
  onPageClick?: () => void,
  startEnabled: SignerContextShape['startEnabled'],
  // TODO: Figure out what the actual type is
  validationErrors: SignerContextShape['validationErrors'],
  defaultDateFormat: string,
  getInstantValidationErrorHack: SignerContextShape['getInstantValidationErrorHack'],
  documentPreview?: boolean,
}

export const signerContext = React.createContext<SignerContextShape>({} as SignerContextShape);


function useLinkNotification(currentFieldId: string, fields: Field[]) {
  const { createBannerMessage, removeBannerMessage } = useLocalBanner();
  const intl = useIntl();
  const dispatch = useDispatch();

  const currentField = React.useMemo(
    () => fields.find((field) => field.id === currentFieldId),
    [currentFieldId, fields],
  );
  const numLinkedFields = React.useMemo(
    () => {
      if (currentField && currentField.linkId) {
        return fields.filter((f) => (
          f.linkId
          && f.id !== currentField.id
          && f.linkId === currentField.linkId
        )).length;
      }
      return 0;
    },
    [currentField, fields],
  );

  React.useEffect(() => {
    if (currentField && numLinkedFields > 0 && currentField.autoFillType) {
      const id = createBannerMessage(
        messages.alertTextAutofill,
        NotificationBannerType.Info,
        {
          fieldNumber: numLinkedFields,
          autoFillType: autofillFieldsTitle[currentField.autoFillType],
        },
        {
          key: 'linked-fields',
          timeout: 4000,
        },
      );
      return () => removeBannerMessage(id);
    }
    return () => { };
  }, [createBannerMessage, currentField, dispatch, intl, numLinkedFields, removeBannerMessage]);
}

const NOOP = () => { };
export default function SignerContext({
  signatureRequest,
  children,
  selectedFieldIds,
  setCurrentField,
  onFieldUpdate = NOOP,
  onPageClick,
  startEnabled,
  validationErrors,
  defaultDateFormat,
  getInstantValidationErrorHack,
  documentPreview = false,
  fieldResetKey,
}: Props) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const contextCacher = useContextCacher<SignerContextShape>();
  const { createBannerMessage } = useLocalBanner();

  const normalizeField = React.useCallback((f) => {
    if (f.type === 'date') {
      // set the date format that is passed in
      let dateFormat = defaultDateFormat;
      // if the field has a format specified and is not default use it
      if (f.dateFormat != null && f.dateFormat !== 'default') {
        dateFormat = f.dateFormat;
      }
      return {
        ...f,
        value: f.value || moment().format(dateFormat),
      };
    }
    return f;
  }, [defaultDateFormat]);
  const [{ fields, hiddenFieldsWithValues, rules }, setState] = React.useState(() => {
    const fields = signatureRequest.fields.map(normalizeField);
    return { fields, hiddenFieldsWithValues: 0, rules: signatureRequest.rules };
  });

  const onFieldUpdateQueue = React.useRef<Parameters<typeof onFieldUpdate>[]>([]);

  /**
   * This component was originally designed to receive fields, and then it
   * maintains its own copy by applying updates locally. For DocumentPreview,
   * this needs a way to pass a different set of fields without having to just
   * use a `key={}` that resets everything.
   */
  React.useEffect(() => {
    if (fieldResetKey) {
      onFieldUpdateQueue.current.length = 0; // reset the queue before resetting the fields
      const fields = signatureRequest.fields.map(normalizeField);
      setState({ fields, hiddenFieldsWithValues: 0, rules: signatureRequest.rules });
    }

    // This needs to reset when fieldResetKey changes but NOT when
    // signatureRequest changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizeField, fieldResetKey]);
  useSkipHiddenFields(fields, selectedFieldIds, setCurrentField);
  useLinkNotification(selectedFieldIds[0], fields);

  React.useEffect(() => {
    if (hiddenFieldsWithValues > 0) {
      createBannerMessage(
        messages.hiddenFieldsNotification,
        NotificationBannerType.Info,
        { hiddenFields: hiddenFieldsWithValues },
        {
          key: 'hidden-fields',
          timeout: 4000,
        },
      );

      setState((state) => (
        { ...state, hiddenFieldsWithValues: 0 }
      ));
    }
  }, [createBannerMessage, dispatch, hiddenFieldsWithValues, intl, signatureRequest.rules]);

  const updateFields = React.useCallback((updates) => {

    setState((state) => {
      let hiddenFieldsWithValues = 0;

      const checkHidden = (field: Field, updates: Partial<Field>) => {
        if (updates.hidden != null) {
          if (updates.hidden) {
            switch (field.type) {
              case FieldTypes.Radio:
              case FieldTypes.Checkbox:
                if (field.checked) {
                  hiddenFieldsWithValues++;
                }
                break;
              case FieldTypes.Dropdown:
              case FieldTypes.Hyperlink:
              case FieldTypes.Text:
                if (field.value) {
                  hiddenFieldsWithValues++;
                }

                break;
              case FieldTypes.Initials:
              case FieldTypes.Signature:
                if (field.signature) {
                  hiddenFieldsWithValues++;
                }
                break;
              case FieldTypes.Date:
                // Dates don't need to notify the user that a value was removed,
                // because it's not a value the user provides.
                break;
              default:
                unreachable(field);
            }
          }
        }

        onFieldUpdateQueue.current.push([field, updates]);
      };


      let radioUpdates: undefined | Record<RadioField['group'], RadioField['id']>;
      let linkIdsToValues: undefined | Record<NonNullable<Field['linkId']>, Partial<Field>>;

      // collect link ids for each field update
      function collectLinkedFieldUpdates(field: Partial<Field>, update: FieldUpdate): void {
        if ('value' in update && field.linkId) {
          linkIdsToValues = {
            ...linkIdsToValues,
            [field.linkId]: update,
          };
        }
      }

      function mergeLinkedFieldUpdates(
        fields: Field[], linkIdsToValues: undefined | Record<NonNullable<Field['linkId']>, Partial<Field>>,
      ) {

        if (linkIdsToValues === null) {
          return fields;
        }
        return fields.map((f) => {
          if (f.linkId && linkIdsToValues && linkIdsToValues[f.linkId]) {
            checkHidden(f, linkIdsToValues[f.linkId]);
            return { ...f, ...linkIdsToValues[f.linkId] };
          }
          return f;
        });

      }

      let fields = state.fields.map((f) => {
        if (updates[f.id] != null) {
          if (f.type === 'radiobutton' && updates[f.id].checked) {
            radioUpdates = {
              ...radioUpdates,
              [f.group]: f.id,
            };
          }
          collectLinkedFieldUpdates(f, updates[f.id]);
          checkHidden(f, updates[f.id]);

          return { ...f, ...updates[f.id] };
        }
        return f;
      });
      fields = mergeLinkedFieldUpdates(fields, linkIdsToValues);
      if (radioUpdates) {
        const update = { checked: false };
        fields = fields.map((f) => {
          // uncheck all the other items in the group
          if (radioUpdates && radioUpdates[f.group] && f.id !== radioUpdates[f.group]) {
            checkHidden(f, update);
            return { ...f, ...update };
          }
          return f;
        });
      }

      return { fields, hiddenFieldsWithValues, rules: signatureRequest.rules };
    });

  }, [signatureRequest.rules]);

  React.useEffect(() => {
    // A previous version attempted to store this queue in state, next to
    // fields. That caused some weird race conditions where some updates got
    // lost. Text fields fire one change for the value and a separate change for
    // lines and font size. Those two updates happen in the same tick, so the
    // queue was updated twice before the next render could happen and run the
    // queue.
    const queue = onFieldUpdateQueue.current;
    while (queue.length > 0) {
      // This needs to use a queue becuase onFieldUpdate needed to be moved
      // outside the `setState()` callback inside `updateFields()`. React
      // didn't like it that `onFieldUpdate` changes DocumentPreview's state
      // while in the middle of a state update here in SignerSignatureDocument.
      //
      // Warning: Cannot update a component from inside the function body of a different component.
      const [field, updates] = queue.shift()!;
      onFieldUpdate(field, updates);
    }
  }/* Run on EVERY render */);

  const [activeSignatures, setActiveSignatures] = React.useState<SignerContextShape['activeSignatures']>({
    signature: null,
    initials: null,
  });

  const visibleFields = React.useMemo(
    () => fields.filter((f) => f.hidden !== true),
    [fields],
  );

  const fieldCounts = React.useMemo(
    () => visibleFields.reduce((counts, fieldData) => {
      if (fieldData.type === 'signature' || fieldData.type === 'initials') {
        if (!fieldData.readOnly && fieldData.signature == null) {
          if (fieldData.type === 'signature') {
            counts.emptySignatureIds.push(fieldData.id);
          } else {
            counts.emptyInitialIds.push(fieldData.id);
          }
        }
      }
      return counts;
    }, {
      emptySignatureIds: [] as Array<Field['id']>,
      emptyInitialIds: [] as Array<Field['id']>,
    }),
    [visibleFields],
  );
  const emptySignatureIds = useShallowCache(fieldCounts.emptySignatureIds);
  const emptyInitialIds = useShallowCache(fieldCounts.emptyInitialIds);

  const insertSignature = React.useCallback(
    (signature, fieldData, insertEverywhere) => {
      let fieldsToUpdate = [fieldData.id];
      if (insertEverywhere) {
        fieldsToUpdate = (fieldData.type === 'signature'
          ? emptySignatureIds
          : emptyInitialIds);
      }

      const updates = fieldsToUpdate.reduce((updates, id) => {
        updates[id] = { signature };
        return updates;
      }, {});

      // if "clearing" the field, let's not null the property since signature is passed as null
      if (signature != null) {
        const fieldType = fieldData.type;
        if (!documentPreview) {
          setActiveSignatures((state) => ({
            ...state,
            [fieldType]: signature,
          }));
        }
      }

      updateFields(updates);
    },
    [documentPreview, emptyInitialIds, emptySignatureIds, updateFields],
  );

  useConditionalLogic(
    fields,
    rules,
    // this hook will call `updateFields` any time something needs to change.
    updateFields,
  );

  const handlePageClick = React.useCallback(() => {
    if (document.activeElement != null) {
      // @ts-ignore TS insists there is no blur
      document.activeElement.blur();
    }
    setCurrentField(null);

    if (onPageClick) {
      onPageClick();
    }
  }, [onPageClick, setCurrentField]);

  const value: SignerContextShape = contextCacher({
    emptySignatureCount: emptySignatureIds.length,
    emptyInitialsCount: emptyInitialIds.length,
    startEnabled,
    validationErrors,
    updateFields: startEnabled ? updateFields : NOOP,
    selectField: startEnabled ? setCurrentField : NOOP,
    insertSignature,
    onPageClick: handlePageClick,
    getInstantValidationErrorHack,
    activeSignatures,
  });

  return (

    <signerContext.Provider value={value}>
      <SignatureRequestContext
        selectedFieldIds={selectedFieldIds}
        dpi={signatureRequest.dpi}
        pages={signatureRequest.pages}
        documentPreview={documentPreview}
        fields={visibleFields}
      >
        {documentPreview && (
          <ValidationNotification />
        )}
        {children}
      </SignatureRequestContext>
    </signerContext.Provider>
  );
}
